import PropTypes from 'prop-types';
import { useMemo } from 'react';
// @mui
import { CssBaseline } from '@mui/material';
import { ThemeProvider as MUIThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
//
import palette from './palette';
import shadows from './shadows';
import typography from './typography';
import GlobalStyles from './globalStyles';
import customShadows from './customShadows';
import componentsOverride from './overrides';


ThemeProvider.propTypes = {
  children: PropTypes.node,
};

/**
 * ThemeProvider is a wrapper component that provides MUI theme to its children.
 *
 * By default, it uses the theme options from the `themeOptions` object, which
 * is created by the `useMemo` hook. The theme options include values for
 * breakpoints, palette, shape, typography, shadows, and custom shadows.
 *
 * The theme is created by calling the `createTheme` function from `@mui/material`
 * and passing the theme options as an argument. The theme is then passed to
 * the `MUIThemeProvider` component, which sets the theme for all its children.
 *
 * The `ThemeProvider` component also renders the `CssBaseline` component,
 * which sets the CSS properties to their default values. Finally, it renders
 * the `GlobalStyles` component, which sets the global styles for the application.
 *
 * @param {object} children - The children elements to be wrapped with the theme.
 * @returns {React.ReactElement} The ThemeProvider component.
 */
export default function ThemeProvider({ children }) {
  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      palette,
      shape: { borderRadius: 6 },
      typography,
      shadows: shadows(),
      customShadows: customShadows(),
    }),
    []
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
